import React, { useState } from 'react';

import DownloadIcon from 'icons/download.svg';
import { useTranslation } from 'react-i18next';

import { useContentRegisterQuery } from 'components/hooks/contentRegisterQueries';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import DownloadModalWrapper from 'components/application/DownloadModalWrapper';

import CategoriesTable from 'components/contentRegister/components/CategoriesTable';
import CustomPersons from 'components/contentRegister/components/customPersons/components/CustomPersons';
import { useContentRegisterContext } from 'components/contentRegister/contexts/ContentRegisterContext';
import useRiskAssessmentsPdfDownload from 'components/contentRegister/hooks/useRiskAssessmentsPdfDownload';
import { useCurrentActor } from 'components/contexts/CurrentActor';

export default function ContentRegister() {
  const { personsAtRiskPolicy, riskRegisterPolicy, seedContentRegister } =
    useContentRegisterContext();

  const { user: currentUser } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'contentRegister.components.ContentRegister',
  });
  const { t: i18nTCommon } = useTranslation('common');

  const [selectedMasterActivityIds, setSelectedMasterActivityIds] = useState(
    () => new Set(),
  );

  const {
    data: domainContentRegister,
    isError: isDomainContentRegisterFetchError,
    isLoading: isDomainContentRegisterLoading,
  } = useContentRegisterQuery({
    queryOptions: {
      initialData: seedContentRegister,
      meta: { errorMessage: i18nT('errors.registerUnavailable') },
    },
  });

  const {
    closeModal,
    downloadAction,
    downloadStatus,
    isModalOpen,
    requestDownload,
    resetDownload,
    retryDownload,
  } = useRiskAssessmentsPdfDownload();

  const handleMasterActivityToggle = (masterActivityId) => {
    const selected = new Set(selectedMasterActivityIds);

    if (selected.has(masterActivityId)) {
      selected.delete(masterActivityId);
    } else {
      selected.add(masterActivityId);
    }

    setSelectedMasterActivityIds(selected);
  };

  const handleCategoryDestroy = () => {
    setSelectedMasterActivityIds(new Set());
  };

  const handleDownloadAllClick = () => {
    requestDownload(downloadableMasterActivityIds);
  };

  const handleDownloadSelectedClick = () => {
    requestDownload([...selectedMasterActivityIds]);
  };

  if (isDomainContentRegisterFetchError) {
    return (
      <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5 tw-text-grey-600'>
        {i18nT('errors.registerUnavailable')}
      </div>
    );
  }

  if (isDomainContentRegisterLoading) {
    return (
      <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5 tw-text-grey-600'>
        {i18nTCommon('shared.loading...')}
      </div>
    );
  }

  const domainTrades = domainContentRegister.data.relationships.trades.data.map(
    ({ id: tradeId }) => domainContentRegister.included.trade[tradeId],
  );

  const downloadableMasterActivityIds = [
    ...new Set(
      domainTrades.flatMap((trade) =>
        trade.relationships.masterActivityTrades.data.map(
          ({ id: masterActivityTradeId }) =>
            domainContentRegister.included.masterActivityTrade[
              masterActivityTradeId
            ].relationships.masterActivity.data.id,
        ),
      ),
    ),
  ];

  return (
    <>
      {(riskRegisterPolicy.canShow || personsAtRiskPolicy.canIndex) && (
        <div className='tw-flex tw-h-12 tw-items-center tw-rounded-t-md tw-border-1 tw-border-b-0 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-5'>
          {riskRegisterPolicy.canShow && (
            <>
              <OutlinedButton
                className='tw-mr-2'
                color='grey'
                disabled={selectedMasterActivityIds.size === 0}
                onClick={handleDownloadSelectedClick}
                size='sm'
              >
                <DownloadIcon height={16} width={16} />
                <span className='tw-ml-2'>{i18nT('downloadSelected')}</span>
              </OutlinedButton>
              <OutlinedButton
                className='tw-mr-4'
                color='grey'
                disabled={downloadableMasterActivityIds.length === 0}
                onClick={handleDownloadAllClick}
                size='sm'
              >
                <DownloadIcon height={16} width={16} />
                <span className='tw-ml-2'>
                  {i18nTCommon('shared.downloadAll')}
                </span>
              </OutlinedButton>
            </>
          )}
          {personsAtRiskPolicy.canIndex && <CustomPersons />}
        </div>
      )}
      <CategoriesTable
        domainContentRegister={domainContentRegister}
        onCategoryDestroy={handleCategoryDestroy}
        onMasterActivityToggle={handleMasterActivityToggle}
        selectedMasterActivityIds={selectedMasterActivityIds}
      />
      {isModalOpen && (
        <DownloadModalWrapper
          analyticProps={{ currentUser }}
          analyticTrackingId='Risk assessments PDF downloaded'
          closeModal={closeModal}
          downloadAction={downloadAction}
          downloadStatus={downloadStatus}
          modalIsOpen={isModalOpen}
          resetAction={resetDownload}
          retryAction={retryDownload}
        />
      )}
    </>
  );
}
